import type { ColumnDef } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { Typography } from "@/components/ui/atoms/Typography";
import { Tag } from "@/components/ui/atoms/Tag";
import { Button } from "@/components/ui/atoms/Button";
import { IconWrapper } from "@/components/ui/atoms/IconWrapper";
import { dataTableSortHeader } from "@/components/ui/molecules/DataTable";
import { formatDate } from "@/utils/date";

export interface Coupon {
  id: string;
  code: string;
  discount_percent: number;
  max_uses: number | null;
  current_uses: number;
  expires_at: string | null;
  linked_email: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CouponTableRow {
  id: string;
  code: string;
  discountPercent: number;
  currentUses: number;
  usesDisplay: string;
  expiresAt: string | null;
  linkedEmail: string;
  emailLockDisplay: string;
  statusDisplay: "Active" | "Inactive";
  isActive: boolean;
  coupon: Coupon;
}

export function mapCouponToRow(coupon: Coupon): CouponTableRow {
  return {
    id: coupon.id,
    code: coupon.code,
    discountPercent: coupon.discount_percent,
    currentUses: coupon.current_uses,
    usesDisplay:
      coupon.max_uses != null
        ? `${coupon.current_uses} / ${coupon.max_uses}`
        : `${coupon.current_uses} / --`,
    expiresAt: coupon.expires_at,
    linkedEmail: coupon.linked_email || "",
    emailLockDisplay: coupon.linked_email || "--",
    statusDisplay: coupon.is_active ? "Active" : "Inactive",
    isActive: coupon.is_active,
    coupon,
  };
}

export interface CouponTableColumnsOptions {
  onEdit: (coupon: Coupon) => void;
  onDeactivate: (id: string) => void;
}

export function createCouponTableColumns({
  onEdit,
  onDeactivate,
}: CouponTableColumnsOptions): ColumnDef<CouponTableRow>[] {
  return [
    {
      accessorKey: "code",
      meta: { label: "Code" },
      header: dataTableSortHeader("Code"),
      size: 140,
      minSize: 110,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="semibold" color="primary" className="font-mono">
          {row.original.code}
        </Typography>
      ),
    },
    {
      accessorKey: "discountPercent",
      meta: { label: "Discount" },
      header: dataTableSortHeader("Discount"),
      size: 110,
      minSize: 90,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="medium" color="primary">
          {row.original.discountPercent}%
        </Typography>
      ),
    },
    {
      accessorKey: "currentUses",
      meta: { label: "Uses" },
      header: dataTableSortHeader("Uses"),
      size: 110,
      minSize: 90,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="medium" color="primary">
          {row.original.usesDisplay}
        </Typography>
      ),
    },
    {
      accessorKey: "expiresAt",
      meta: { label: "Expires" },
      header: dataTableSortHeader("Expires"),
      size: 140,
      minSize: 110,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="medium" color="muted">
          {formatDate(row.original.expiresAt)}
        </Typography>
      ),
    },
    {
      accessorKey: "linkedEmail",
      meta: { label: "Email lock" },
      header: dataTableSortHeader("Email lock"),
      size: 180,
      minSize: 140,
      cell: ({ row }) => (
        <Typography variant="body-sm" weight="medium" color="muted">
          {row.original.emailLockDisplay}
        </Typography>
      ),
    },
    {
      accessorKey: "statusDisplay",
      meta: { label: "Status" },
      header: dataTableSortHeader("Status"),
      size: 120,
      minSize: 100,
      cell: ({ row }) => (
        <Tag intent={row.original.isActive ? "success" : "error"} variant="subtle">
          {row.original.statusDisplay}
        </Tag>
      ),
    },
    {
      id: "actions",
      meta: { label: "Actions" },
      header: () => (
        <span className="block text-center font-heading text-xs font-bold uppercase tracking-wider text-dark-invirtus">
          Actions
        </span>
      ),
      size: 120,
      minSize: 100,
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => (
        <div
          className="flex justify-center gap-1"
          onClick={(event) => event.stopPropagation()}
        >
          <Button
            type="icon"
            intent="muted"
            size="sm"
            title="Edit"
            aria-label={`Edit ${row.original.code}`}
            onClick={() => onEdit(row.original.coupon)}
            icon={
              <IconWrapper size="sm">
                <Pencil />
              </IconWrapper>
            }
          />
          {row.original.isActive ? (
            <Button
              type="icon"
              intent="danger-soft"
              size="sm"
              title="Deactivate"
              aria-label={`Deactivate ${row.original.code}`}
              onClick={() => onDeactivate(row.original.id)}
              icon={
                <IconWrapper size="sm">
                  <Trash2 />
                </IconWrapper>
              }
            />
          ) : null}
        </div>
      ),
    },
  ];
}
